import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import NavigationMinimal from '../components/layout/NavigationMinimal';
import { GalleryCardSkeletonList } from '../components/gallery';
import GalleryFilter from '../components/gallery/GalleryFilter';
import { fetchGalleryAlbums } from '../services/galleryService';
import { buildGalleryAlbumRoute } from '../constants/routes';
import '../components/gallery/Gallery.css';

const formatDate = (dateString) => {
  try {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(new Date(dateString));
  } catch {
    return dateString;
  }
};

const GalleryPage = () => {
  const [albums, setAlbums] = useState([]);
  const [status, setStatus] = useState('loading');
  const [searchParams, setSearchParams] = useSearchParams();

  const activeYear = searchParams.get('year');

  const availableYears = useMemo(() => {
    const years = new Set();
    albums.forEach((album) => {
      if (album.date) {
        const year = album.date.substring(0, 4);
        if (/^\d{4}$/.test(year)) {
          years.add(year);
        }
      }
    });
    return [...years].sort((a, b) => b - a);
  }, [albums]);

  const filteredAlbums = useMemo(() => {
    if (!activeYear) return albums;
    return albums.filter((album) => album.date && album.date.startsWith(activeYear));
  }, [albums, activeYear]);

  const handleYearChange = (year) => {
    if (year) {
      setSearchParams({ year });
    } else {
      setSearchParams({});
    }
  };

  useEffect(() => {
    let isMounted = true;

    fetchGalleryAlbums()
      .then((albumList) => {
        if (!isMounted) return;
        setAlbums(albumList);
        setStatus('ready');
      })
      .catch(() => {
        if (isMounted) {
          setStatus('error');
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const showAlbums = status === 'ready' && albums.length > 0;
  const hasFilteredResults = showAlbums && filteredAlbums.length > 0;
  const showEmptyFilter = showAlbums && !hasFilteredResults;

  return (
    <>
      <NavigationMinimal />
      <div className="sub-hero">
        <span className="sub-hero__eyebrow">Rückblicke &amp; Highlights</span>
        <h1 className="sub-hero__title">Galerie</h1>
        <p className="sub-hero__lead">
          Einblicke in Turniere, Community-Momente und Lieblingsplätze im KickerEck. Stöbere durch die
          Alben der letzten Monate und finde deine Lieblingsmomente wieder.
        </p>
      </div>
      <main className="page-shell page-shell--after-hero gallery-page">

        <section className="gallery-section" aria-label="Albenübersicht">
          {status === 'loading' && <GalleryCardSkeletonList />}

          {status === 'error' && (
            <div className="gallery-status gallery-status--error" role="alert" data-testid="gallery-error-message">
              Die Galerie konnte aktuell nicht geladen werden. Bitte versuche es später erneut.
            </div>
          )}

          {!showAlbums && status === 'ready' && (
            <div className="gallery-status">
              Noch keine Alben verfügbar. Schau bald wieder vorbei!
            </div>
          )}

          {showAlbums && (
            <div className="gallery-content-fade">
              <GalleryFilter
                years={availableYears}
                activeYear={activeYear}
                onYearChange={handleYearChange}
              />

              {showEmptyFilter && (
                <p className="gallery-filter-empty" data-testid="gallery-filter-empty">
                  Keine Alben in diesem Jahr.
                </p>
              )}

              {hasFilteredResults && (
                <div className="gallery-card-grid">
                  {filteredAlbums.map((album) => (
                    <div key={album.id}>
                      <article className="gallery-card">
                        <Link
                          to={buildGalleryAlbumRoute(album.id)}
                          className="gallery-card-link"
                          aria-label={`Album ${album.title} öffnen`}
                        >
                          {album.coverPhoto && (
                            <img
                              src={album.coverPhoto.thumbUrl || album.coverPhoto.fullUrl}
                              alt={album.coverPhoto.caption || album.title}
                              loading="lazy"
                            />
                          )}
                          <div className="gallery-card-body">
                            <span className="gallery-card-date">{formatDate(album.date)}</span>
                            <h2 className="gallery-card-title">{album.title}</h2>
                            <p className="gallery-card-description">{album.description}</p>
                            <div className="gallery-card-footer">
                              <span>{album.photoCount} Fotos</span>
                              <span className="gallery-card-cta">Album ansehen →</span>
                            </div>
                          </div>
                        </Link>
                      </article>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </main>
    </>
  );
};

export default GalleryPage;
